import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message, Modal } from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ExpandAltOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AddTeamForm from "../team/addTeam";
import ViewTeamDetail from "../team/viewTeamDetails";
import EditTeam from "../team/editTeam";
import "./teamsV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const apiUrl = process.env.REACT_APP_API_Team;

const TeamsV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [teams, setTeams] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewModalVisible, setviewModalVisible] = useState(false);
  const [editModalVisible, setEidtModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchData();
  }, [open, editModalVisible]);

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch teams");
      const data = await response.json();
      setTeams(data.body || []);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="teams-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="teams-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "teams-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleViewClick = (record) => {
    setviewModalVisible(true);
    setTeamId(record.id);
  };

  const handleEditClick = (record) => {
    setEidtModalVisible(true);
    setTeamId(record.id);
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${apiUrl}/${selectedRecord.id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        messageApi.success("Team deleted successfully");
        setTeams(teams.filter((r) => r.id !== selectedRecord.id));
      } else {
        messageApi.error("Failed to delete team");
      }
    } catch (error) {
      messageApi.error("Failed to delete team");
    }
    setDeleteModalVisible(false);
  };

  const handleAddTesterClick = () => setOpen(true);

  const columns = [
    {
      title: "Team Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      ...getColumnSearchProps("name"),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Country",
      dataIndex: "country_name",
      key: "country_name",
      width: "25%",
      ...getColumnSearchProps("country_name"),
      sorter: (a, b) => (a.country_name || "").localeCompare(b.country_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Actions",
      key: "actions",
      width: "45%",
      render: (_, record) => (
        <div className="teams-v2-actions">
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewClick(record)}
            className="teams-v2-btn-view"
          >
            View
          </Button>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            className="teams-v2-btn-edit"
          >
            Edit
          </Button>
          <Button
            type="default"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
            danger
            className="teams-v2-btn-delete"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="teams-v2">
      {contextHolder}
      <div className="teams-v2-header">
        <h1 className="teams-v2-title">All Teams</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddTesterClick}
          className="teams-v2-add-btn"
        >
          Add Team
        </Button>
      </div>

      <div className="teams-v2-card">
        <Table
            columns={columns}
            dataSource={teams}
            loading={loading}
            rowKey="id"
            locale={{ emptyText: loading ? "Loading..." : "No teams yet" }}
            pagination={{
              showSizeChanger: true,
              defaultPageSize: 10,
              pageSizeOptions: ["5", "10", "20", "50"],
              showTotal: (total) => `Total ${total} teams`,
            }}
            className="teams-v2-table"
          />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        className="teams-v2-delete-modal"
        width={420}
      >
        <p className="teams-v2-delete-text">
          Are you sure you want to delete <strong>{selectedRecord?.name}</strong>? This action cannot be undone.
        </p>
      </Modal>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="teams-v2-dialog"
        PaperProps={{
          className: "teams-v2-dialog-paper",
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AddTeamForm setOpen={setOpen} />
      </Dialog>

      <Dialog
        open={viewModalVisible}
        onClose={() => setviewModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="teams-v2-dialog"
        PaperProps={{
          className: "teams-v2-dialog-paper",
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <ViewTeamDetail setOpen={setviewModalVisible} teamId={teamId} />
      </Dialog>

      <Dialog
        open={editModalVisible}
        onClose={() => setEidtModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="teams-v2-dialog"
        PaperProps={{
          className: "teams-v2-dialog-paper",
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <EditTeam setOpen={setEidtModalVisible} id={teamId} />
      </Dialog>
    </div>
  );
};

export default TeamsV2;
